import Form, { useForm } from 'antd/lib/form/Form';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Link from 'antd/lib/typography/Link';
import Text from 'antd/lib/typography/Text';
import FormItem from 'antd/lib/form/FormItem';
import Tooltip from 'antd/lib/tooltip';
import Space from 'antd/lib/space';
import { DownOutlined, InfoCircleOutlined, ThunderboltFilled } from '@ant-design/icons';
import Radio from 'antd/lib/radio';
import { convertAndroidStagedRolloutPercentage, displayStoreNameForAppStoreType } from '../../helpers/displayHelpers';
import { AppStoreType, GoogleAppStoreSettings } from '../../types';
import { AndroidStagedRolloutStyle } from '../../typescriptHelpers';
import { stagedRolloutPercentageInputValidationRules } from '@/helpers/formHelpers';
import { AndroidStagedRolloutPercentageInput } from '../sharedComponents/shared';
import Dropdown from 'antd/es/dropdown/dropdown';
import { Button } from 'antd/lib';
import { GoogleInAppUpdatePriorities, getOrDefaultInAppUpdatePriority } from '@/helpers/appSettingsHelpers';

interface EditGoogleReleaseSettingsProps {
    isConfiguringAppDefaults: boolean;
    open: boolean;
    isLoading: boolean;
    customStagedRolloutEnabled: boolean;
    isReleased?: boolean;
    customStagedRolloutDaysToPercent?: number[];
    googleAppStoreSettings?: GoogleAppStoreSettings;
    onSubmit: (stagedRelease: boolean, inAppUpdatePriority: number, initialStagedRolloutPercentage?: number) => void;
    onCancel: () => void;
}

const EditGoogleReleaseSettings: React.FC<EditGoogleReleaseSettingsProps> = (props: EditGoogleReleaseSettingsProps) => {
    const [form] = useForm();
    const [stagedReleaseEnabled, setStagedReleaseEnabled] = React.useState(false);
    const [inAppUpdatePriority, setInAppUpdatePriority] = React.useState(
        getOrDefaultInAppUpdatePriority(props.googleAppStoreSettings?.inAppUpdatePriority),
    );

    const title =
        props.isConfiguringAppDefaults ?
            `Edit ${displayStoreNameForAppStoreType(AppStoreType.Google)} release defaults`
        :   `Edit release settings`;

    const description =
        props.isConfiguringAppDefaults ?
            'Choose a default staged rollout behavior for any new release submissions.'
        :   'Update the release settings for this release.';

    const requiresInitialStagedRollout =
        !props.customStagedRolloutEnabled ||
        (props.customStagedRolloutEnabled && !props.customStagedRolloutDaysToPercent);

    const shouldDisableUpdatePriorityEditing = props.isConfiguringAppDefaults || props.isReleased;
    const menuItems = Object.entries(GoogleInAppUpdatePriorities).map(([key, label]) => ({
        label,
        key,
        disabled: inAppUpdatePriority === parseInt(key),
    }));

    const onSubmit = (values) => {
        let stagedRelease = false;

        switch (values.androidStagedReleaseStyle as AndroidStagedRolloutStyle) {
            case AndroidStagedRolloutStyle.Staged:
                stagedRelease = true;
                break;
            case AndroidStagedRolloutStyle.NotStaged:
                stagedRelease = false;
                break;
        }

        const initialStagedRolloutPercentage =
            values.initialStagedRolloutPercentage ? values.initialStagedRolloutPercentage : null;

        props.onSubmit(stagedRelease, inAppUpdatePriority, initialStagedRolloutPercentage);
    };

    React.useEffect(() => {
        if (props.open) {
            if (props.googleAppStoreSettings) {
                const androidStagedReleaseStyle =
                    props.googleAppStoreSettings.stagedRelease ?
                        AndroidStagedRolloutStyle.Staged
                    :   AndroidStagedRolloutStyle.NotStaged;
                const initialInAppUpdatePriority = getOrDefaultInAppUpdatePriority(
                    props.googleAppStoreSettings?.inAppUpdatePriority,
                );
                let stagedRolloutPercentage;

                if (props.googleAppStoreSettings.initialStagedRolloutPercentage) {
                    stagedRolloutPercentage = convertAndroidStagedRolloutPercentage(
                        props.googleAppStoreSettings.initialStagedRolloutPercentage,
                    );
                } else if (props.customStagedRolloutEnabled) {
                    stagedRolloutPercentage =
                        props.customStagedRolloutDaysToPercent ? props.customStagedRolloutDaysToPercent[0] : null;
                }

                form.setFieldsValue({
                    androidStagedReleaseStyle: androidStagedReleaseStyle,
                    initialStagedRolloutPercentage: stagedRolloutPercentage,
                    inAppUpdatePriority: initialInAppUpdatePriority,
                });

                setStagedReleaseEnabled(androidStagedReleaseStyle === AndroidStagedRolloutStyle.Staged);
                setInAppUpdatePriority(initialInAppUpdatePriority);
            } else {
                form.setFieldsValue({
                    androidStagedReleaseStyle: null,
                    initialStagedRolloutPercentage: null,
                    inAppUpdatePriority: 0,
                });

                setStagedReleaseEnabled(false);
                setInAppUpdatePriority(0);
            }
        }
    }, [props.open]);

    return (
        <Modal
            title={title}
            onOk={() => form.submit()}
            okButtonProps={{ loading: props.isLoading }}
            onCancel={props.onCancel}
            destroyOnClose
            open={props.open}
            okText={'Update'}
            cancelText="Cancel"
            afterClose={() => form.resetFields()}
            bodyStyle={{ backgroundColor: antGrey.grey3 }}
        >
            <Text
                style={{
                    paddingBottom: 12,
                    display: 'block',
                }}
            >
                {description}
            </Text>
            <Form
                form={form}
                layout="vertical"
                onFinish={onSubmit}
                initialValues={{
                    androidStagedReleaseStyle: AndroidStagedRolloutStyle.NotStaged,
                    initialStagedRolloutPercentage: 25,
                    inAppUpdatePriority: 0,
                }}
            >
                <FormItem label="Staged rollout" name="androidStagedReleaseStyle" rules={[{ required: true }]}>
                    <Radio.Group
                        onChange={(e) => {
                            setStagedReleaseEnabled(e.target.value === AndroidStagedRolloutStyle.Staged);
                        }}
                    >
                        <Space direction="vertical">
                            <Radio value={AndroidStagedRolloutStyle.NotStaged}>
                                Don&apos;t stage, release updates to all users immediately
                            </Radio>
                            <Radio value={AndroidStagedRolloutStyle.Staged}>
                                Staged rollout to a percentage of users
                            </Radio>
                        </Space>
                    </Radio.Group>
                </FormItem>
                {stagedReleaseEnabled && (
                    <FormItem
                        name="initialStagedRolloutPercentage"
                        label="Initial staged rollout percent"
                        rules={[
                            { required: requiresInitialStagedRollout },
                            ...stagedRolloutPercentageInputValidationRules,
                        ]}
                        help={
                            props.customStagedRolloutEnabled ?
                                <Text style={{ color: antGrey.grey7 }}>
                                    <ThunderboltFilled style={{ color: runwayColors.primary }} /> Automated custom
                                    staged rollout is enabled. The staged rollout percentage indicated for Day 1 of your
                                    custom staged rollout{' '}
                                    {(
                                        props.customStagedRolloutDaysToPercent &&
                                        props.customStagedRolloutDaysToPercent.length > 0
                                    ) ?
                                        `(${props.customStagedRolloutDaysToPercent[0]}%)`
                                    :   null}{' '}
                                    will be used.{' '}
                                    <Tooltip
                                        title={
                                            <Space direction="vertical">
                                                <Space direction="vertical" size={0}>
                                                    {props.customStagedRolloutDaysToPercent?.map((percent, i) => {
                                                        return (
                                                            <Text style={{ color: 'white' }} key={i}>
                                                                Day {i + 1}: {percent}%
                                                            </Text>
                                                        );
                                                    })}
                                                </Space>
                                                <Text style={{ color: 'white' }}>
                                                    You can configure your custom staged rollout from automation
                                                    settings.
                                                </Text>
                                            </Space>
                                        }
                                    >
                                        <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                                    </Tooltip>
                                </Text>
                            :   null
                        }
                    >
                        <AndroidStagedRolloutPercentageInput disabled={!requiresInitialStagedRollout} />
                    </FormItem>
                )}
                {!props.isConfiguringAppDefaults && (
                    <FormItem
                        label="Set update priority"
                        name="inAppUpdatePriority"
                        tooltip={{
                            title: (
                                <Text style={{ color: runwayColors.white }}>
                                    This allows your app to decide how strongly to recommend this update to the user,
                                    with 0 being the default and 5 being the highest priority. For more information,
                                    visit Google Play's
                                    <Text> </Text>
                                    <Link
                                        href="https://developer.android.com/guide/playcore/in-app-updates/native#update-priority"
                                        target="_blank"
                                        style={{ color: runwayColors.white, textDecoration: 'underline' }}
                                    >
                                        documentation
                                    </Link>
                                    .
                                </Text>
                            ),
                            icon: (
                                <Space style={{ marginLeft: 8 }}>
                                    <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                                </Space>
                            ),
                        }}
                    >
                        <Dropdown
                            disabled={shouldDisableUpdatePriorityEditing}
                            menu={{
                                onClick: (item) => setInAppUpdatePriority(parseInt(item.key)),
                                items: menuItems,
                            }}
                        >
                            <Tooltip
                                title="Update priority cannot be changed once the release is rolled out."
                                placement="right"
                                trigger={shouldDisableUpdatePriorityEditing ? ['hover'] : []}
                            >
                                <Button disabled={shouldDisableUpdatePriorityEditing}>
                                    <Space>
                                        <Text
                                            style={{
                                                color:
                                                    shouldDisableUpdatePriorityEditing ?
                                                        antGrey.grey6
                                                    :   runwayColors.black,
                                            }}
                                        >
                                            {menuItems[inAppUpdatePriority].label}
                                        </Text>
                                        <DownOutlined
                                            style={{
                                                color:
                                                    shouldDisableUpdatePriorityEditing ?
                                                        antGrey.grey6
                                                    :   runwayColors.primary,
                                            }}
                                        />
                                    </Space>
                                </Button>
                            </Tooltip>
                        </Dropdown>
                    </FormItem>
                )}
            </Form>
            {props.isConfiguringAppDefaults && (
                <Text type="secondary">These settings will apply to all releases for this app.</Text>
            )}
        </Modal>
    );
};

export default EditGoogleReleaseSettings;
